<template>
  <div class="lanes">
    <div class="columns">
      <div class="column pane">
        <h3>Lanes</h3>
        <Draggable v-model="lanesSorted" class="content list" draggable=".lane.item">
          <div
            :class="['lane item', { active: lane.enabled }]"
            v-for="(lane, idx) of lanesSorted"
            :key="lane.id"
            draggable="true"
            @click="editLane(lane)">
            <div class="tip">
              <div :class="['icon', 'emoji', getLaneIconClass(lane)]" />
            </div>
            <div class="head">
              <strong class="title">{{ lane.title }}</strong>
              <small class="count">
                {{ laneSubmissions(lane).length }}
                <i class="icon fas fa-folder" />
              </small>
            </div>
            <small class="description">{{ lane.description }}</small>
          </div>
          <div slot="footer" class="lane new active" @click="editLane()">
            <div class="icon emoji plus" />
            <span>Create new lane</span>
          </div>
        </Draggable>
      </div>

      <!-- Editor -->
      <div class="column editor" v-if="editing">
        <!-- Lane -->
        <div class="pane lane">
          <h3>Editor</h3>
          <div class="content">
            <div class="row">
              <label>Title</label>
              <input
                type="text"
                v-model="selectedLane.title"
                placeholder="Some Queue"
                class="title" />
            </div>
            <div class="row">
              <label>Description</label>
              <input
                type="text"
                v-model="selectedLane.description"
                placeholder="This is a queue for special cards or something."
                class="description" />
            </div>
            <div class="row">
              <label>Type</label>
              <select
                v-model="selectedLane.template"
                :disabled="!!selectedLane.id">
                <option value="mtga">MTGA Decks</option>
                <!-- <option value="hearthstone">Hearthstone Decks</option> -->
                <option value="games">Video Games</option>
                <option value="generic">Generic</option>
              </select>
            </div>
            <div class="row">
              <label>Enabled</label>
              <select v-model="selectedLane.enabled">
                <option :value="true" :disabled="!canEnable">
                  <template v-if="canEnable">
                    Yes &ndash; Visible in the extension.
                  </template>
                  <template v-else>
                    Yes &ndash; At least one product must be offered.
                  </template>
                </option>
                <option :value="false">
                  No &ndash; Hidden in the extension, not available for new submissions.
                </option>
              </select>
            </div>
            <div class="row">
              <label>Locked</label>
              <select v-model="selectedLane.locked">
                <option :value="true">Locked &ndash; Not accepting new submissions.</option>
                <option :value="false">Open &ndash; Accepting new submissions.</option>
              </select>
            </div>
            <hr />
            <button
              @click="saveLane(selectedLane)"
              v-text="selectedLane.id ? 'Save' : 'Create'" />

            <button
              v-if="selectedLane.id"
              @click="toggleModal()"
              :disabled="remainingActiveSubmissions.length === 0"
              v-text="'Refund/move submissions'" />

            <button
              v-if="selectedLane.id"
              :disabled="remainingActiveSubmissions.length > 0"
              @click="deleteLane(selectedLane)"
              v-text="'Delete'" />

            <div v-if="selectedLaneIsDirty" class="save-reminder">
              Changes have been made, don't forget to save them!
            </div>
          </div>
        </div>

        <!-- Products -->
        <div class="pane products">
          <h3>Products</h3>
          <LaneItems
            class="content"
            :template="selectedLane.template"
            :items="selectedLane.products"
            :type="'product'" />
        </div>

        <!-- Upsells -->
        <div class="pane upsells">
          <h3>Upsells</h3>
          <LaneItems
            class="content"
            :template="selectedLane.template"
            :items="selectedLane.upsells"
            :type="'upsell'" />
        </div>

      </div>
    </div>
    <div
      class="modal"
      :class="{ active: modalActive }"
      @click.self="toggleModal()">
      <div class="pane lane-action">
        <h1>{{ selectedLane.title }}</h1>
        <p>
          There are <strong>{{ remainingActiveSubmissions.length }}</strong>
          submissions remaining in this lane that haven't been archived.
        </p>
        <p>
          What do you want to do with them?
        </p>

        <select
          v-model="laneAction"
          @change="resetDefaultLaneActionMessage()">
          <option :value="{ action: 'purge' }">Delete and refund points</option>
          <template v-for="lane of lanes">
            <option
              :key="lane.id"
              v-if="lane.id !== selectedLane.id"
              :disabled="lane.template !== selectedLane.template"
              :value="{ action: 'move', destination: lane }"
              v-text="`Move them to ${lane.title}`" />
          </template>
        </select>

        <br />

        <label>
          <p>
            <input
              type="checkbox"
              v-model="laneActionWithMessage"
              @change="resetDefaultLaneActionMessage()"
              :value="true" />
            Include a message for submission authors
          </p>
        </label>

        <fieldset v-if="laneActionWithMessage">
          <legend>Message</legend>

          <div>
            <strong>Subject:</strong>
            <br />
            <input
              v-model="laneActionSubject"
              type="text"
              placeholder="Subject" />
          </div>

          <div>
            <strong>Body:</strong>
            <br />
            <textarea
              v-if="laneActionWithMessage"
              v-model="laneActionMessage"
              @change="laneActionMessageDirty = true"
              placeholder="Message" />
          </div>
        </fieldset>

        <button @click="submitLaneAction()">Do it!</button>

        <!-- <Submissions :submissions="remainingActiveSubmissions" style="max-width: 800px" /> -->
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import _ from 'lodash'
import Draggable from 'vuedraggable'
import LaneItems from '@/components/LaneItems'
import ApiHelper from '@/mixins/ApiHelper'
import DeckHelper from '@/mixins/DeckHelper'
import { DECK_STATUS } from '@/constants'

export default {
  name: 'Lanes',
  mixins: [ApiHelper, DeckHelper],
  components: {
    LaneItems,
    Draggable
  },

  data () {
    return {
      editing: false,
      selectedLane: {},
      modalActive: false,
      laneAction: { action: 'purge' },
      laneActionWithMessage: true,
      laneActionSubject: '',
      laneActionMessage: '',
      laneActionMessageDirty: false
    }
  },

  computed: {
    ...mapState(['lanes', 'upsells', 'submissions']),
    lanesSorted: {
      get () {
        return this.lanes
          .slice(0)
          .sort((a, b) => Math.sign((a.order || 0) - (b.order || 0)))
      },
      set (lanes) {
        const data = lanes.map((lane) => lane.id)
        this.apiRequest('lane/reorder', {
          method: 'post',
          data
        })
      }
    },

    canEnable () {
      if (!this.selectedLane) {
        return false
      }

      return this.selectedLane.products.length > 0
    },

    remainingActiveSubmissions () {
      const activeStatuses = [
        DECK_STATUS.ACTIVE,
        DECK_STATUS.APPROVED,
        DECK_STATUS.PENDING
      ]
      return this.laneSubmissions(this.selectedLane)
        .filter((submission) => {
          const status = this.getStatus(submission)
          return activeStatuses.includes(status)
        })
    },

    defaultLaneActionMessage () {
      switch (this.laneAction.action) {
        case 'purge':
          return 'Your submission has been deleted and all points refunded.'
        case 'move': {
          const destLane = this.laneAction.destination.title
          return `Your submission has been moved to ${destLane}.`
        }
      }
      return ''
    },

    // Returns true/false if editing lane is different from db storage
    selectedLaneIsDirty () {
      const blob = this.dumbifyLane(this.selectedLane)
      return !this.lanes
        .some((lane) => _.isEqual(this.dumbifyLane(lane), blob))
    }
  },

  methods: {
    getLaneIconClass (lane) {
      if (!lane.enabled) {
        return 'times'
      }
      if (lane.locked) {
        return 'archived'
      }
      return 'approved'
    },

    editLane (lane) {
      const target = Object.assign({
        title: '',
        description: '',
        template: 'generic',
        products: [],
        upsells: [],
        enabled: false,
        locked: false
      }, lane || {})
      if (lane && lane.id) {
        target.id = lane.id
        target.products = lane.products.map((el) => ({ ...el }))
        target.upsells = lane.upsells.map((el) => ({ ...el }))
      }
      this.selectedLane = target
      this.editing = true
    },

    saveLane (lane) {
      const copy = { ...lane }
      if (!copy.id) {
        copy.order = this.lanes.length
      }
      this.apiRequest('lane', {
        method: 'post',
        data: {
          ...copy,
          // products: lane.products.map((p) => p.key),
          // upsells: lane.upsells.map((u) => u.key)
        }
      })
      if (!copy.id) {
        this.editing = false
      }
    },

    deleteLane (lane) {
      if (!confirm(`Are you sure you want to delete "${lane.title}"?`)) {
        return false
      }

      this.apiRequest(`lane/${lane.id}`, {
        method: 'delete'
      })

      this.editing = false
    },

    laneSubmissions (lane) {
      return this.submissions
        .filter((submission) => submission.lane === lane.id)
    },

    toggleModal () {
      this.laneActionMessageDirty = false
      this.laneAction = { action: 'purge' }
      this.resetDefaultLaneActionMessage()
      this.modalActive = !this.modalActive
    },

    resetDefaultLaneActionMessage () {
      if (this.laneActionMessageDirty) {
        return
      }

      this.laneActionSubject = `The ${this.selectedLane.title} queue has closed`
      this.laneActionMessage = this.defaultLaneActionMessage
    },

    submitLaneAction () {
      const laneId = this.selectedLane.id
      const action = this.laneAction.action
      let url = `lane/${laneId}/${action}`
      if (action === 'move') {
        url += `/${this.laneAction.destination.id}`
      }
      const data = { mail: false }
      if (this.laneActionWithMessage) {
        if (!this.laneActionSubject || !this.laneActionMessage) {
          return alert('Both subject and body are required for mails!')
        }
        Object.assign(data, {
          mail: {
            subject: this.laneActionSubject,
            message: this.laneActionMessage
          }
        })
      }

      this.apiRequest(url, {
        method: 'post',
        data
      })
      .then(() => this.modalActive && this.toggleModal())
      .catch((err) => alert(err.toString()))
    },

    // Returns lane with reduced set of props for comparison purposes - no
    // timestamps or other background-specific metadata
    dumbifyLane (lane) {
      const worthwhileProps = [
        'title',
        'description',
        'products',
        'upsells',
        'enabled',
        'locked'
      ]
      const mappedProps = worthwhileProps.map((p) => ({ [p]: lane[p] }))
      return Object.assign({}, ...mappedProps)
    }
  },

  watch: {
    'selectedLane.products' () {
      if (this.selectedLane.products.length === 0) {
        this.selectedLane.enabled = false
      }
    }
  }
}
</script>

<style scoped>
.pane > h3 {
  margin: 0;
  padding: 0.5em;
  background: #eee;
  border-bottom: 1px solid #999;
}
.columns {
  display: grid;
  grid-template-columns: 300px auto;
  grid-gap: 1em;
}
.column {
  overflow: hidden;
}
/* .column > .content {
  padding: 0.5em;
} */
.list .lane {
  display: grid;
  grid-template-columns: 2em 1fr;
  grid-auto-rows: 2;
  align-items: center;
  cursor: pointer;
  /* grid-template-areas: 'icon title' 'icon description'; */
}
.list .lane.new {
  border-top: 1px solid #999;
  padding-top: 0.5em;
}
.list .lane .tip {
  grid-row: 1 / 3;
}
.list .lane + .lane {
  margin-top: 1em;
}
.list .lane .head {
  display: flex;
  justify-content: space-between;
}
.list .lane .description {
  display: block;
}

.editor {
  display: grid;
  grid-gap: 0.5em;
  grid-template-rows: 1fr auto;
  grid-template-columns: 1fr 1fr;
}
.editor .lane {
  grid-column: 1 / 3;
}
.editor .title {
  font-size: larger;
}
.editor .row {
  display: grid;
  grid-template-columns: 100px 1fr;
  align-items: baseline;
  grid-gap: 0.5em;
  text-align: right;
}
.editor .row + .row {
  margin-top: 1em;
}

.modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: #0006;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 100ms ease-out;
  opacity: 0;
}
.modal:not(.active) {
  pointer-events: none;
}
.modal.active {
  opacity: 1;
}
.modal .pane {
  background: white;
  padding: 1em;
  border-radius: 4px;
  box-shadow: 0 2px 20px #0006;
}
.modal fieldset {
  border: 1px solid #999;
  border-radius: 4px;
  margin: 1em 0;
}

.lane-action {
  max-width: 800px;
}
.lane-action textarea {
  width: 100%;
  min-height: 200px;
}

.save-reminder {
  margin: 1rem 0 0;
  padding: 1em;
  background: #f66;
  color: white;
  border-radius: 4px;
  box-shadow: inset 0 0 0 1px red;
  font-size: smaller;
}

</style>
